import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { wormColors } from "core/src/core/constants.js";

import KeyPicker from "../../../shared/key-picker/KeyPicker";
import { useKeyChainFocus } from "../../../shared/useKeyChainFocus";
import { loadWhoCount, saveWhoCount } from "../../../shared/local-players";
import { useLocalRoster } from "./hooks";
import { Swatches } from "./parts";
import s from "./start.module.scss";

// "Who's playing?": asked on the way into Play Now, on a
// device with a keyboard. Six slots: yours is taken, and every free one is a
// plus that seats another player on this keyboard (a colour, a name and two
// steering keys each). Ranked and the Royale never ask: they seat one player
// per device.
//
// `onPick(n)`: how many of the device's players go in (the first n of the
// shared store, shared/local-players). The count is the one offered next time,
// so playing alone stays one press of the button.
export default function WhoPlays({ go = "Play", max, onPick, onCancel }: {
  go?: string;
  max?: number;
  onPick: (count: number) => void;
  onCancel: () => void;
}) {
  const roster = useLocalRoster();
  const { players, setName, setColor, setKey, addPlayer, removePlayer } = roster;
  const cap = Math.min(max ?? roster.max, roster.max);
  // How many slots are taken. The store may remember more players than that
  // (friends from last time): they come back, as they were, when their slot is.
  const [count, setCount] = useState(() => Math.min(cap, loadWhoCount()));
  const [swSlot, setSwSlot] = useState<number | null>(null); // whose colours are open
  const { advance, focusToken } = useKeyChainFocus();

  // A slot just taken that the store has no player for yet: make one.
  useEffect(() => { if (players.length < count) addPlayer(); }, [count, players.length, addPlayer]);

  const shown = players.slice(0, count);
  // Nobody goes in without both steering keys: Play then points at the players
  // who lack one instead of starting.
  const lacksKeys = (p: { left: string | null; right: string | null }) => !p.left || !p.right;
  const [warn, setWarn] = useState(false);
  const missing = shown.some(lacksKeys);
  useEffect(() => { if (!missing) setWarn(false); }, [missing]);
  const pick = () => {
    if (missing) { setWarn(true); return; }
    saveWhoCount(shown.length); onPick(shown.length);
  };
  const remove = (i: number) => { removePlayer(i); setCount((n) => Math.max(1, n - 1)); setSwSlot(null); };

  // Enter plays, Escape backs out (unless a key picker is using that key).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter" && tag !== "BUTTON") pick();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  return createPortal(
    <div className={s.whoScrim} onClick={onCancel}>
      <div className={s.who} role="dialog" aria-label="Who's playing?" onClick={(e) => { e.stopPropagation(); if (!(e.target as HTMLElement).closest("[data-sw]")) setSwSlot(null); }}>
        <button type="button" className={`${s.popX} ${s.whoX}`} onClick={onCancel} aria-label="Close" data-sound="back">×</button>
        <h2 className={s.whoTitle}>Who's playing?</h2>
        <p className={s.whoSub}>You can have multiple keyboard warriors</p>
        <div className={s.whoSlots}>
          {Array.from({ length: cap }, (_, i) => {
            const p = shown[i];
            if (!p) {
              // a free slot: taking it seats everyone up to it
              return <button type="button" key={i} className={s.whoFree} onClick={() => { setCount(i + 1); setSwSlot(null); }} aria-label="Add player">+</button>;
            }
            return (
              <div key={i} className={s.whoSlot} style={{ ["--c" as any]: (wormColors as any)[p.colorId] }}>
                {i > 0 && <button type="button" className={s.whoOut} onClick={() => remove(i)} aria-label={`Remove ${p.name}`}>×</button>}
                <button type="button" data-sw="" className={s.whoDot} onClick={() => setSwSlot(swSlot === i ? null : i)} aria-label="Colour" aria-expanded={swSlot === i} />
                {/* the colours drop down over the card: nothing in it moves */}
                {swSlot === i && <div className={`${s.swDrop} ${s.whoDrop} ${i % 3 === 0 ? s.whoDropL : i % 3 === 2 ? s.whoDropR : ""}`} data-sw=""><Swatches player={p} players={shown} onPick={(c) => { setColor(i, c); setSwSlot(null); }} /></div>}
                <input className={s.whoName} value={p.name} maxLength={16} onChange={(e) => setName(i, e.target.value)} aria-label={i === 0 ? "Your name" : `Player ${i + 1} name`} spellCheck={false} />
                <span className={`${s.pKeys} ${warn && lacksKeys(p) ? s.whoKeysMissing : ""}`} aria-label="Steering keys">
                  <KeyPicker currentKey={p.left ?? undefined} onKeyPicked={(k: string) => { setKey(i, "left", k); advance(String(i), "left"); }} autoSelectToken={focusToken(String(i), "left")} />
                  <KeyPicker currentKey={p.right ?? undefined} onKeyPicked={(k: string) => { setKey(i, "right", k); advance(String(i), "right"); }} autoSelectToken={focusToken(String(i), "right")} />
                </span>
              </div>
            );
          })}
        </div>
        {warn && <div className={s.whoWarn} role="alert">{shown.length > 1 ? "Every player needs two steering keys" : "Set your two steering keys first"}</div>}
        <button type="button" className={s.whoGo} onClick={pick} data-sound="confirm" autoFocus>{go}</button>
      </div>
    </div>,
    document.body
  );
}
